import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, Table, Typography, Sheet, IconButton, Modal, ModalDialog,
  ModalClose, Select, Option, Chip, Alert
} from '@mui/joy';
import { getProducts, deleteProduct } from '../../services/api';
import ProductForm from './ProductForm';
import SearchInput from './SearchInput';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import InventoryIcon from '@mui/icons-material/Inventory';

function ProductManager({ categories = [], onRefresh }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('id');

  const [alert, setAlert] = useState(null);

  const showAlert = (message, color = 'success') => {
    setAlert({ message, color });
    setTimeout(() => setAlert(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      showAlert('Не удалось загрузить товары', 'danger');
      setProducts([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : '—';
  };

  const filteredProducts = useMemo(() => {
    let result = [...products];
    const term = search.trim().toLowerCase();
    if (term) {
      result = result.filter(p => p.name.toLowerCase().includes(term));
    }
    if (categoryFilter !== 'all') {
      result = result.filter(p => String(p.category_id) === String(categoryFilter));
    }
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'price':
        result.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        break;
      case 'id':
      default:
        result.sort((a, b) => (a.id || 0) - (b.id || 0));
    }
    return result;
  }, [products, search, categoryFilter, sortBy]);

  const handleOpenAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleOpenEdit = (product) => {
    setEditing(product);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSave = async (data) => {
    try {
      const { createProduct, updateProduct } = await import('../../services/api');
      if (editing) {
        await updateProduct(editing.id, data);
        showAlert('Товар обновлён');
      } else {
        await createProduct(data);
        showAlert('Товар добавлен');
      }
      handleCloseForm();
      fetchData();
      if (onRefresh) onRefresh();
    } catch (e) {
      showAlert('Ошибка сохранения: ' + (e.response?.data?.error || e.message), 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить товар?')) return;
    try {
      await deleteProduct(id);
      showAlert('Товар удалён');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (e) {
      showAlert('Ошибка удаления: ' + (e.response?.data?.error || e.message), 'danger');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography level="h3">Товары</Typography>
        <Button startDecorator={<AddIcon />} onClick={handleOpenAdd}>Добавить товар</Button>
      </Box>

      {alert && (
        <Alert color={alert.color} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 16, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Поиск по названию..."
          />
          <Select
            value={categoryFilter}
            onChange={(e, val) => setCategoryFilter(val || 'all')}
            startDecorator={<FilterListIcon />}
            sx={{ minWidth: 180 }}
          >
            <Option value="all">Все категории</Option>
            {categories.map(c => (
              <Option key={c.id} value={String(c.id)}>{c.name}</Option>
            ))}
          </Select>
          <Select
            value={sortBy}
            onChange={(e, val) => setSortBy(val || 'id')}
            sx={{ minWidth: 160 }}
          >
            <Option value="id">Сортировка: ID</Option>
            <Option value="name">Сортировка: название</Option>
            <Option value="price">Сортировка: цена</Option>
          </Select>
        </Box>
      </Sheet>

      {loading ? (
        <Typography>Загрузка...</Typography>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 16, overflow: 'hidden' }}>
          <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th style={{ width: 60 }}>ID</th>
                <th style={{ width: 70 }}>Фото</th>
                <th>Название</th>
                <th>Категория</th>
                <th style={{ width: 100 }}>Цена</th>
                <th style={{ width: 90 }}>Кол-во</th>
                <th style={{ width: 110 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <Box sx={{ py: 6, textAlign: 'center', color: 'neutral.500' }}>
                      <InventoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                      <Typography>Товары не найдены</Typography>
                    </Box>
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>
                      <Box
                        component="img"
                        src={p.image || (p.images && p.images[0])}
                        alt=""
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid',
                          borderColor: 'neutral.outlinedBorder',
                          bgcolor: 'background.body',
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </td>
                    <td>
                      <Typography level="body-sm" fontWeight="md">{p.name}</Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft" color="primary">
                        {getCategoryName(p.category_id)}
                      </Chip>
                    </td>
                    <td>{p.price} Br</td>
                    <td>{p.quantity ?? '—'}</td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="sm" variant="soft" color="primary" onClick={() => handleOpenEdit(p)}><EditIcon /></IconButton>
                        <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(p.id)}><DeleteIcon /></IconButton>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      <Modal open={showForm} onClose={handleCloseForm}>
        <ModalDialog
          variant="outlined"
          sx={{
            width: { xs: '95%', md: 800, lg: 900 },
            maxHeight: '90vh',
            overflow: 'auto',
            borderRadius: 20,
            p: 0,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="h4">
              {editing ? 'Редактировать товар' : 'Добавить товар'}
            </Typography>
            <ModalClose onClick={handleCloseForm} />
          </Box>
          <Box sx={{ p: 3 }}>
            <ProductForm
              initialData={editing}
              categories={categories}
              onSave={handleSave}
              onCancel={handleCloseForm}
            />
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default ProductManager;
